import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Download, Copy, Check } from 'lucide-react';
import { useState } from 'react';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  voucherCode: string;
  participantName?: string;
}

const QRCodeModal: React.FC<QRCodeModalProps> = ({ 
  isOpen, 
  onClose, 
  voucherCode, 
  participantName 
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(voucherCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar código:', err);
    }
  };

  const downloadQRCode = () => {
    // Encontrar o elemento SVG do QR Code
    const svg = document.querySelector('#qr-code-svg');
    if (!svg) return;

    // Criar um canvas para converter SVG em imagem
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      canvas.width = 300;
      canvas.height = 300;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 0, 0, 300, 300);

      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `qrcode-${voucherCode}.png`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
        }
      });
      URL.revokeObjectURL(svgUrl);
    };
    img.src = svgUrl;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">QR Code do Voucher</h2>
            {participantName && (
              <p className="text-sm text-gray-600 mt-1">{participantName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* QR Code */}
        <div className="p-6">
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6 flex items-center justify-center mb-6">
            <QRCodeSVG
              id="qr-code-svg"
              value={voucherCode}
              size={200}
              level="M"
              includeMargin={false}
              className="w-full h-full max-w-[200px] max-h-[200px]"
            />
          </div>

          {/* Voucher Code */}
          <div className="text-center mb-6">
            <p className="text-sm text-gray-600 mb-2">Código do Voucher:</p>
            <div className="flex items-center justify-center space-x-2">
              <code className="bg-gray-100 px-3 py-2 rounded-lg font-mono text-sm font-semibold text-gray-900">
                {voucherCode}
              </code>
              <button
                onClick={copyToClipboard}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copiar código"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4 text-gray-600" />
                )}
              </button>
            </div>
            {copied && (
              <p className="text-xs text-green-600 mt-1">Código copiado!</p>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-blue-900 mb-2">💡 Como usar</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Apresente este QR Code na recepção do hotel</li>
              <li>• O código também pode ser digitado manualmente</li>
              <li>• Mantenha uma cópia salva no seu dispositivo</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={downloadQRCode}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Baixar QR Code
            </button>
            <button
              onClick={onClose}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;
